import { useEffect, useRef, useState, useCallback } from "react";
import Phaser from "phaser";
import BootScene from "./scenes/BootScene";
import MainScene from "./scenes/MainScene";
import Minimap from "./Minimap";
import CharacterSelectScreen from "./CharacterSelectScreen";
import GameListScreen from "./GameListScreen";
import BestiaryScreen from "./BestiaryScreen";
import BiomeGalleryScreen from "./BiomeGalleryScreen";
import InventoryScreen, { ItemIcon, hasIconFrame } from "./InventoryScreen";
import QuestsScreen from "./QuestsScreen";
import TravelHubScreen from "./TravelHubScreen";
import ShopScreen from "./ShopScreen";
import HotbarScreen from "./HotbarScreen";
import CraftingScreen from "./CraftingScreen";
import ChestScreen from "./ChestScreen";
import TouchControls from "./TouchControls";
import { computeLevelFromXp } from "./leveling";
import { resolveAbilityDef } from "./abilityDefs";
import { resolveItemDef } from "./itemDefs";
import { fetchMyGames, abandonGame, deleteGame } from "../../api/arpgClient";
import AttributesScreen from "./AttributesScreen";
import FullMapScreen from "./FullMapScreen";

/**
 * Overlay sombre qui se dissout progressivement au-dessus d'un
 * emplacement de la barre en recharge - transition CSS pilotee UNE
 * SEULE fois (pas de boucle JS qui recalcule a chaque frame).
 * `key={startedAt}` sur le parent (cf. plus bas) force un vrai
 * remontage a chaque nouvelle recharge, meme pour la MEME competence/
 * objet - sinon une recharge declenchee en plein milieu d'une
 * precedente ne redemarrerait jamais proprement l'animation depuis
 * opacite 1.
 */
function HotbarCooldownOverlay({ startedAt, cooldownMs }) {
  const [opacity, setOpacity] = useState(1);
  useEffect(() => {
    setOpacity(1);
    const raf = requestAnimationFrame(() => setOpacity(0));
    return () => cancelAnimationFrame(raf);
  }, [startedAt, cooldownMs]);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        opacity,
        transition: `opacity ${cooldownMs}ms linear`,
        pointerEvents: "none",
      }}
    />
  );
}

/**
 * Barre de statistique compacte (vie/mana/stamina/XP) - remplace les
 * anciens indicateurs textuels espaces, qui debordaient sur mobile des
 * que mana ET stamina etaient tous les deux presents (le bouton
 * "sauvegarder et quitter" disparaissait alors hors ecran). La valeur
 * reste affichee, mais SUR la barre plutot qu'a cote.
 */
function StatBar({ value, max, color, label, width }) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div
      style={{
        position: "relative",
        width,
        height: 13,
        background: "rgba(0,0,0,0.45)",
        borderRadius: 4,
        overflow: "hidden",
        border: "1px solid #444",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          width: `${pct}%`,
          background: color,
          transition: "width 0.2s",
        }}
      />
      <div
        style={{
          position: "relative",
          fontSize: 9,
          color: "#fff",
          textAlign: "center",
          lineHeight: "13px",
          textShadow: "0 0 2px #000, 0 0 2px #000",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </div>
    </div>
  );
}
const AZERTY_DIGIT_MAP = {
  "&": 1,
  é: 2,
  '"': 3,
  "'": 4,
  "(": 5,
  "-": 6,
  è: 7,
  _: 8,
  ç: 9,
};

export default function Arpg() {
  const containerRef = useRef(null);
  const gameRef = useRef(null);
  const arpgContainerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [isMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(pointer: coarse) and (hover: none)").matches,
  );
  const [isPortrait, setIsPortrait] = useState(
    () =>
      typeof window !== "undefined" && window.innerHeight > window.innerWidth,
  );

  useEffect(() => {
    if (!isMobile) return;

    const handleOrientationChange = () =>
      setIsPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener("resize", handleOrientationChange);
    window.addEventListener("orientationchange", handleOrientationChange);

    if (window.screen.orientation && window.screen.orientation.lock) {
      window.screen.orientation.lock("landscape").catch(() => {});
    }

    return () => {
      window.removeEventListener("resize", handleOrientationChange);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, [isMobile]);
  useEffect(() => {
    function handleFullscreenChange() {
      setIsFullscreen(!!document.fullscreenElement);
    }
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
    };
  }, []);

  const lootToastTimerRef = useRef(null);

  const [phase, setPhase] = useState("loading");
  const [games, setGames] = useState([]);
  const [heroId, setHeroId] = useState(null);
  const [resumeSave, setResumeSave] = useState(null);
  const [levelReady, setLevelReady] = useState(false);

  const [playerHp, setPlayerHp] = useState({ hp: 100, maxHp: 100 });
  const [playerMana, setPlayerMana] = useState({ mana: 0, maxMana: 0 });
  const [playerStamina, setPlayerStamina] = useState({
    stamina: 0,
    maxStamina: 0,
  });
  const [xp, setXp] = useState(0);
  const [level, setLevel] = useState(1);
  const [depth, setDepth] = useState(1);
  const [gameOver, setGameOver] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [minimapData, setMinimapData] = useState(null);
  const [npcDialog, setNpcDialog] = useState(null);
  const [quests, setQuests] = useState({});
  const [chestScreenData, setChestScreenData] = useState(null);
  const [upstairsPrompt, setUpstairsPrompt] = useState(null);
  const [exitPrompt, setExitPrompt] = useState(null);
  const [resummonPrompt, setResummonPrompt] = useState(null);
  const [summonReplacePrompt, setSummonReplacePrompt] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [equipped, setEquipped] = useState({
    mainHand: null,
    offHand: null,
    armor: null,
    helmet: null,
    pants: null,
    boots: null,
    belt: null,
    ring1: null,
    ring2: null,
    necklace: null,
    quiver: null,
    tool: null,
  });
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [fullMapOpen, setFullMapOpen] = useState(false);
  const [travelDestinations, setTravelDestinations] = useState(null);
  const [shopStock, setShopStock] = useState(null);
  const [questsOpen, setQuestsOpen] = useState(false);
  const [lootToast, setLootToast] = useState(null);
  const [minimapVisible, setMinimapVisible] = useState(true);
  const minimapWasVisibleRef = useRef(true);
  const overlayActiveRef = useRef(false); // vrai tant qu'AU MOINS un ecran est ouvert, meme en changeant de l'un a l'autre

  const hideMinimapForOverlay = useCallback(() => {
    if (!overlayActiveRef.current) {
      minimapWasVisibleRef.current = minimapVisible;
      overlayActiveRef.current = true;
    }
    setMinimapVisible(false);
  }, [minimapVisible]);

  const restoreMinimapAfterOverlay = useCallback(() => {
    overlayActiveRef.current = false;
    setMinimapVisible(minimapWasVisibleRef.current);
  }, []);

  const [keyboardLayout, setKeyboardLayoutState] = useState("azerty");
  const [username, setUsername] = useState(null);

  const [hotbarSlots, setHotbarSlots] = useState(new Array(9).fill(null));
  const [cooldownEvents, setCooldownEvents] = useState({});
  const [unlockedAbilities, setUnlockedAbilities] = useState([]);
  const [hotbarScreenOpen, setHotbarScreenOpen] = useState(false);

  const [furyProgress, setFuryProgress] = useState({ count: 0, required: 10 });

  const [unlockedRecipes, setUnlockedRecipes] = useState([]);
  const [discoveredLockedRecipes, setDiscoveredLockedRecipes] = useState([]);
  const [craftingScreenOpen, setCraftingScreenOpen] = useState(false);
  const closeOtherOverlays = useCallback(
    (exceptReason) => {
      const scene = gameRef.current?.scene.getScene("MainScene");
      if (exceptReason !== "inventory" && inventoryOpen) {
        setInventoryOpen(false);
        if (scene) scene.unpauseGame("inventory");
      }
      if (exceptReason !== "quests" && questsOpen) {
        setQuestsOpen(false);
        if (scene) scene.unpauseGame("quests");
      }
      if (exceptReason !== "hotbar" && hotbarScreenOpen) {
        setHotbarScreenOpen(false);
        if (scene) scene.unpauseGame("hotbar");
      }
      if (exceptReason !== "crafting" && craftingScreenOpen) {
        setCraftingScreenOpen(false);
        if (scene) scene.unpauseGame("crafting");
      }
      if (exceptReason !== "fullMap" && fullMapOpen) {
        setFullMapOpen(false);
        if (scene) scene.unpauseGame("fullMap");
      }
    },
    [
      inventoryOpen,
      questsOpen,
      hotbarScreenOpen,
      craftingScreenOpen,
      fullMapOpen,
    ],
  );
  const [levelUpAvailable, setLevelUpAvailable] = useState(false);
  const [levelUpScreenOpen, setLevelUpScreenOpen] = useState(false);
  const [levelUpDraft, setLevelUpDraft] = useState({
    confirmed: {},
    draft: {},
    unspent: 0,
  });
  const [combatStats, setCombatStats] = useState({ level: 1 });
  const [focusedButtonIndex, setFocusedButtonIndex] = useState(0);

  useEffect(() => {
    setFocusedButtonIndex(0);
  }, [exitPrompt, upstairsPrompt, resummonPrompt, npcDialog]);

  const loadGamesList = () => {
    fetchMyGames()
      .then(({ games, username }) => {
        setGames(games || []);
        setUsername(username || null);
      })
      .catch(() => setGames([]))
      .finally(() => setPhase("picker"));
  };

  useEffect(() => {
    loadGamesList();
  }, []);

  useEffect(() => {
    if (phase !== "playing" || !heroId) return;

    const config = {
      type: Phaser.AUTO,
      width: 1000,
      height: 750,
      parent: containerRef.current,
      pixelArt: true,
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
        width: 1000,
        height: 750,
      },
      audio: { noAudio: true },
      physics: {
        default: "arcade",
        arcade: { gravity: { y: 0 }, debug: true },
      },
      scene: [BootScene, MainScene],
    };

    let destroyed = false;

    const game = new Phaser.Game(config);
    gameRef.current = game;

    // plein ecran automatique au lancement/reprise d'une partie - le DOM
    // de la phase "playing" (arpgContainerRef) vient d'etre monte par
    // React juste avant que cet effet ne s'execute, donc la ref est deja
    // valide ici (contrairement a handleSelectHero/handleResumeGame, ou
    // on est encore en phase "select"/"picker" et cette div n'existe pas
    // encore). Cible arpgContainerRef (le conteneur du jeu) et non
    // document.documentElement, pour ne pas mettre toute la page
    // (navbar/footer inclus) en plein ecran.
    if (arpgContainerRef.current && !document.fullscreenElement) {
      (
        arpgContainerRef.current.requestFullscreen ||
        arpgContainerRef.current.webkitRequestFullscreen
      )?.call(arpgContainerRef.current);
    }

    game.registry.set("heroId", heroId);
    if (resumeSave) game.registry.set("resumeSave", resumeSave);
    game.registry.set("isMobile", isMobile);

    game.events.once("ready", () => {
      if (destroyed) return;
      const scene = game.scene.getScene("MainScene");
      if (!scene) return;

      scene.events.on("player-hp-changed", ({ hp, maxHp }) =>
        setPlayerHp({ hp, maxHp }),
      );
      scene.events.on("player-mana-changed", ({ mana, maxMana }) =>
        setPlayerMana({ mana, maxMana }),
      );
      scene.events.on("player-stamina-changed", ({ stamina, maxStamina }) =>
        setPlayerStamina({ stamina, maxStamina }),
      );
      scene.events.on("xp-changed", ({ xp }) => setXp(xp));
      scene.events.on("level-up", ({ level }) => setLevel(level));
      scene.events.on("game-over", ({ xp, depth }) =>
        setGameOver({ xp, depth }),
      );
      scene.events.on("level-loading", () => setLoadError(null));
      scene.events.on("level-load-error", ({ error }) => setLoadError(error));

      const handleFirstLevelLoaded = () => {
        setLevelReady(true);
        scene.events.off("level-loaded", handleFirstLevelLoaded); // ne s'ecoute qu'une seule fois - pas a chaque changement d'etage ensuite
      };
      scene.events.on("level-loaded", handleFirstLevelLoaded);

      scene.events.on("level-loaded", ({ depth }) => {
        setGameOver(null);
        setDepth(depth);
      });
      scene.events.on("fog-changed", (data) => setMinimapData(data));
      scene.events.on("npc-dialog", (dialog) => setNpcDialog(dialog));
      scene.events.on("quests-updated", (qs) => setQuests(qs));
      scene.events.on("loot-toast", (text) => {
        setLootToast(text);
        clearTimeout(lootToastTimerRef.current);
        lootToastTimerRef.current = setTimeout(() => setLootToast(null), 3500);
      });
      scene.events.on("levelup-available", ({ available }) =>
        setLevelUpAvailable(available),
      );
      scene.events.on("levelup-screen-open", (data) => {
        if (data) {
          setLevelUpDraft({
            confirmed: data.attributes,
            draft: data.draftAttributes,
            unspent: data.unspent,
          });
          setLevelUpScreenOpen(true);
        } else {
          setLevelUpScreenOpen(false);
        }
      });
      scene.events.on("levelup-draft-updated", ({ attributes, unspent }) =>
        setLevelUpDraft((prev) => ({ ...prev, draft: attributes, unspent })),
      );
      scene.events.on("attributes-updated", ({ attributes, unspent }) =>
        setLevelUpDraft((prev) => ({ ...prev, confirmed: attributes })),
      );
      scene.events.on("upstairs-prompt", (data) => setUpstairsPrompt(data));
      scene.events.on("exit-prompt", (data) => setExitPrompt(data));
      scene.events.on("resummon-prompt", (data) => setResummonPrompt(data));
      scene.events.on("summon-replace-prompt", (data) =>
        setSummonReplacePrompt(data),
      );
      scene.events.on("inventory-updated", (inv) => setInventory(inv));
      scene.events.on("chest-screen", (data) => setChestScreenData(data));
      scene.events.on("travel-hub", (destinations) =>
        setTravelDestinations(destinations),
      );
      scene.events.on("shop", (stock) => setShopStock(stock));
      scene.events.on("equipment-updated", (eq) => setEquipped(eq));
      scene.events.on("hotbar-updated", (slots) => setHotbarSlots(slots));
      scene.events.on(
        "hotbar-cooldown-started",
        ({ key, cooldownMs, startedAt }) =>
          setCooldownEvents((prev) => ({
            ...prev,
            [key]: { cooldownMs, startedAt },
          })),
      );
      scene.events.on("abilities-updated", (abilities) =>
        setUnlockedAbilities(abilities),
      );
      scene.events.on("fury-progress", ({ count, required }) =>
        setFuryProgress({ count, required }),
      );
      scene.events.on("recipes-updated", (recipes) =>
        setUnlockedRecipes(recipes),
      );
      scene.events.on("locked-recipes-updated", (recipes) =>
        setDiscoveredLockedRecipes(recipes),
      );
      scene.events.on("quit-to-menu", () => {
        if (document.fullscreenElement) {
          (document.exitFullscreen || document.webkitExitFullscreen)?.call(
            document,
          );
        }
        setShopStock(null);
        setTravelDestinations(null);
        setPhase("picker");
        loadGamesList();
      });
      scene.events.on("player-stats-changed", (stats) => setCombatStats(stats));
    });

    return () => {
      destroyed = true;
      game.destroy(true);
      gameRef.current = null;
    };
  }, [phase, heroId, resumeSave, isMobile]);

  const handleRetry = () => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.retryLevel();
  };

  const handleAcceptQuest = useCallback(() => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.acceptQuest();
  }, []);

  const handleTurnInQuest = useCallback(() => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.turnInQuest();
  }, []);

  const handleCloseDialog = useCallback(() => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.closeDialog();
  }, []);
  const handleChooseInteraction = useCallback((index) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.chooseInteraction(index);
  }, []);
  const handleTakeChestItem = useCallback((itemIndex) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.takeChestItem(itemIndex);
  }, []);
  const handleTakeAllChest = useCallback(() => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.takeAllChestItems();
  }, []);
  const handleCloseChestScreen = useCallback(() => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.closeChestScreen();
  }, []);

  const handleSaveAndQuit = () => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (shopStock && scene) scene.closeShop(); // ferme la boutique (et rend la main a la scene) avant de quitter
    if (scene) scene.saveAndQuit();
  };

  const handleConfirmUpstairs = useCallback(() => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.confirmGoUpstairs();
  }, []);

  const handleCancelUpstairs = useCallback(() => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.cancelGoUpstairs();
  }, []);

  const handleConfirmExit = useCallback(() => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.confirmDescend();
  }, []);

  const handleCancelExit = useCallback(() => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.cancelDescend();
  }, []);

  const handleConfirmResummon = useCallback(() => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.confirmResummon();
  }, []);

  const handleCancelResummon = useCallback(() => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.cancelResummon();
  }, []);

  const handleConfirmSummonReplace = useCallback(() => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.confirmSummonReplace();
  }, []);

  const handleCancelSummonReplace = useCallback(() => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.cancelSummonReplace();
  }, []);

  const handleEquip = (index) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.equipItem(index);
  };

  const handleUnequip = (slot) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.unequipItem(slot);
  };

  const handleUseConsumable = (index) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.useConsumable(index);
  };

  const handleDecraftItem = (index) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.decraftItem(index);
  };

  const handleSocketGem = (instanceId, gemItemId, socketIndex) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.socketGem(instanceId, gemItemId, socketIndex);
  };

  const handleRenameEquipment = (instanceId, newName) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.renameEquipmentInstance(instanceId, newName);
  };

  const handleAttemptPerforation = (scrollIndex, targetInstanceId) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.attemptSocketPerforation(scrollIndex, targetInstanceId);
  };

  const handleAttemptGemExtraction = (
    scrollIndex,
    targetInstanceId,
    socketIndex,
  ) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene)
      scene.attemptGemExtraction(scrollIndex, targetInstanceId, socketIndex);
  };

  const handleOpenInventory = useCallback(() => {
    hideMinimapForOverlay();
    closeOtherOverlays("inventory");
    setInventoryOpen(true);
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.pauseGame("inventory");
  }, [hideMinimapForOverlay, closeOtherOverlays]);

  const handleCloseInventory = useCallback(() => {
    setInventoryOpen(false);
    restoreMinimapAfterOverlay();
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.unpauseGame("inventory");
  }, [restoreMinimapAfterOverlay]);

  const handleToggleFullscreen = useCallback(() => {
    const el = arpgContainerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
    } else {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(
        document,
      );
    }
  }, []);

  const handleOpenFullMap = useCallback(() => {
    hideMinimapForOverlay();
    closeOtherOverlays("fullMap");
    setFullMapOpen(true);
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.pauseGame("fullMap");
  }, [hideMinimapForOverlay, closeOtherOverlays]);

  const handleCloseFullMap = useCallback(() => {
    setFullMapOpen(false);
    restoreMinimapAfterOverlay();
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.unpauseGame("fullMap");
  }, [restoreMinimapAfterOverlay]);

  const handleOpenQuests = useCallback(() => {
    hideMinimapForOverlay();
    closeOtherOverlays("quests");
    setQuestsOpen(true);
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.pauseGame("quests");
  }, [hideMinimapForOverlay, closeOtherOverlays]);

  const handleCloseQuests = useCallback(() => {
    setQuestsOpen(false);
    restoreMinimapAfterOverlay();
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.unpauseGame("quests");
  }, [restoreMinimapAfterOverlay]);

  const handleOpenHotbarScreen = useCallback(() => {
    hideMinimapForOverlay();
    closeOtherOverlays("hotbar");
    setHotbarScreenOpen(true);
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.pauseGame("hotbar");
  }, [hideMinimapForOverlay, closeOtherOverlays]);

  const handleCloseHotbarScreen = useCallback(() => {
    setHotbarScreenOpen(false);
    restoreMinimapAfterOverlay();
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.unpauseGame("hotbar");
  }, [restoreMinimapAfterOverlay]);

  const handleAssignHotbarSlot = (index, payload) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.assignHotbarSlot(index, payload);
  };

  const handleOpenCraftingScreen = useCallback(() => {
    hideMinimapForOverlay();
    closeOtherOverlays("crafting");
    setCraftingScreenOpen(true);
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.pauseGame("crafting");
  }, [hideMinimapForOverlay, closeOtherOverlays]);

  const handleCloseCraftingScreen = useCallback(() => {
    setCraftingScreenOpen(false);
    restoreMinimapAfterOverlay();
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.unpauseGame("crafting");
  }, [restoreMinimapAfterOverlay]);

  const handleCraftItem = (recipeId, flexAllocations, baseInstanceId) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.craftItem(recipeId, flexAllocations, baseInstanceId);
  };

  const handleAttemptFreeCraft = (selectedItems) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.attemptFreeCraft(selectedItems);
  };

  const handleToggleKeyboardLayout = () => {
    const next = keyboardLayout === "azerty" ? "qwerty" : "azerty";
    setKeyboardLayoutState(next);
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.setKeyboardLayout(next);
  };

  const handleOpenLevelUpScreen = useCallback(() => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.openLevelUpScreen();
  }, []);

  const handleCloseLevelUpScreen = useCallback(() => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.closeLevelUpScreen();
  }, []);

  const handleAllocatePoint = useCallback((attribute) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.allocateAttributePoint(attribute);
  }, []);
  const handleDeallocatePoint = useCallback((attribute) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.deallocateAttributePoint(attribute);
  }, []);

  const handleConfirmAllocation = useCallback(() => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.confirmAttributeAllocation();
  }, []);
  const getCurrentDialogActions = useCallback(() => {
    if (levelUpScreenOpen) {
      const attrKeys = [
        "force",
        "dexterite",
        "intelligence",
        "vitalite",
        "constitution",
        "endurance",
        "chance",
      ];
      const actions = [];
      for (const key of attrKeys) {
        actions.push({
          label: `attr-${key}-minus`,
          onClick: () => handleDeallocatePoint(key),
        });
        actions.push({
          label: `attr-${key}-plus`,
          onClick: () => handleAllocatePoint(key),
        });
      }
      actions.push({ label: "attr-confirm", onClick: handleConfirmAllocation });
      actions.push({ label: "attr-close", onClick: handleCloseLevelUpScreen });
      return actions;
    }
    if (chestScreenData) {
      const actions = chestScreenData.items.map((item) => ({
        label: `chest-item-${item.itemIndex}`,
        onClick: () => handleTakeChestItem(item.itemIndex),
      }));
      if (chestScreenData.items.length > 0) {
        actions.push({ label: "chest-take-all", onClick: handleTakeAllChest });
      }
      actions.push({ label: "chest-close", onClick: handleCloseChestScreen });
      return actions;
    }
    if (exitPrompt) {
      return [
        { label: "Oui", onClick: handleConfirmExit },
        { label: "Non", onClick: handleCancelExit },
      ];
    }
    if (upstairsPrompt) {
      return [
        { label: "Oui", onClick: handleConfirmUpstairs },
        { label: "Non", onClick: handleCancelUpstairs },
      ];
    }
    if (resummonPrompt) {
      return [
        { label: "Oui", onClick: handleConfirmResummon },
        { label: "Non", onClick: handleCancelResummon },
      ];
    }
    if (summonReplacePrompt) {
      return [
        { label: "Oui", onClick: handleConfirmSummonReplace },
        { label: "Non", onClick: handleCancelSummonReplace },
      ];
    }
    if (npcDialog) {
      const actions = [];
      (npcDialog.choices || []).forEach((c, i) => {
        actions.push({
          label: `choice-${i}`,
          onClick: () => handleChooseInteraction(i),
        });
      });
      if (npcDialog.canAccept)
        actions.push({ label: "Accepter", onClick: handleAcceptQuest });
      if (npcDialog.canTurnIn)
        actions.push({ label: "Rendre", onClick: handleTurnInQuest });
      actions.push({ label: "Fermer", onClick: handleCloseDialog });
      return actions;
    }
    return [];
  }, [
    exitPrompt,
    upstairsPrompt,
    resummonPrompt,
    summonReplacePrompt,
    npcDialog,
    chestScreenData,
    levelUpScreenOpen,
    handleConfirmExit,
    handleCancelExit,
    handleConfirmUpstairs,
    handleCancelUpstairs,
    handleConfirmResummon,
    handleCancelResummon,
    handleConfirmSummonReplace,
    handleCancelSummonReplace,
    handleAcceptQuest,
    handleTurnInQuest,
    handleCloseDialog,
    handleChooseInteraction,
    handleTakeChestItem,
    handleTakeAllChest,
    handleCloseChestScreen,
    handleAllocatePoint,
    handleDeallocatePoint,
    handleConfirmAllocation,
    handleCloseLevelUpScreen,
  ]);

  useEffect(() => {
    function handleGlobalKeyDown(e) {
      if (!gameRef.current) return;

      // ignore les raccourcis globaux tant qu'un champ de texte (ex: le
      // renommage d'objet) a le focus, sinon taper "i" ferme l'inventaire etc.
      const activeTag = document.activeElement?.tagName;
      const isEditableField =
        activeTag === "INPUT" ||
        activeTag === "TEXTAREA" ||
        document.activeElement?.isContentEditable;
      if (isEditableField) return;

      const key = e.key.toLowerCase();

      // navigation au clavier dans un dialogue actif - prioritaire sur
      // tout le reste tant qu'un dialogue est ouvert
      const dialogActions = getCurrentDialogActions();
      if (dialogActions.length > 0) {
        const isLeft = key === "arrowleft" || key === "q" || key === "a";
        const isRight = key === "arrowright" || key === "d";
        if (isLeft) {
          e.preventDefault();
          setFocusedButtonIndex(
            (prev) => (prev - 1 + dialogActions.length) % dialogActions.length,
          );
          return;
        }
        if (isRight) {
          e.preventDefault();
          setFocusedButtonIndex((prev) => (prev + 1) % dialogActions.length);
          return;
        }
        if (key === "e" || key === "enter") {
          e.preventDefault();
          if (key === "e") {
            const scene = gameRef.current?.scene.getScene("MainScene");
            scene?.keys?.action?.reset(); // empeche Phaser de redeclencher performInteraction via cette MEME pression de E
          }
          dialogActions[focusedButtonIndex]?.onClick();
          return;
        }
      }

      if (key === "i") {
        if (inventoryOpen) handleCloseInventory();
        else handleOpenInventory();
      } else if (key === "r") {
        if (questsOpen) handleCloseQuests();
        else handleOpenQuests();
      } else if (key === "f") {
        if (hotbarScreenOpen) handleCloseHotbarScreen();
        else handleOpenHotbarScreen();
      } else if (key === "g") {
        if (craftingScreenOpen) handleCloseCraftingScreen();
        else handleOpenCraftingScreen();
      } else if (key === "h") {
        if (fullMapOpen) handleCloseFullMap();
        else handleOpenFullMap();
      } else if (key === "t") {
        if (levelUpScreenOpen) handleCloseLevelUpScreen();
        else handleOpenLevelUpScreen();
      } else if (key === "tab") {
        e.preventDefault();
        handleToggleFullscreen();
      } else if (key === "v") {
        setMinimapVisible((v) => !v);
      } else if (
        (key >= "1" && key <= "9") ||
        AZERTY_DIGIT_MAP[key] !== undefined
      ) {
        const digit = AZERTY_DIGIT_MAP[key] ?? Number(key);
        const scene = gameRef.current.scene.getScene("MainScene");
        if (scene) scene.useHotbarSlot(digit - 1);
      }
    }
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [
    inventoryOpen,
    questsOpen,
    hotbarScreenOpen,
    craftingScreenOpen,
    fullMapOpen,
    levelUpScreenOpen,
    focusedButtonIndex,
    getCurrentDialogActions,
    handleOpenInventory,
    handleCloseInventory,
    handleOpenQuests,
    handleCloseQuests,
    handleOpenHotbarScreen,
    handleCloseHotbarScreen,
    handleOpenCraftingScreen,
    handleCloseCraftingScreen,
    handleOpenFullMap,
    handleCloseFullMap,
    handleOpenLevelUpScreen,
    handleCloseLevelUpScreen,
    handleToggleFullscreen,
  ]);

  const handleTravelToDepth = (depth) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.travelToDepth(depth);
  };

  const handleCloseTravelHub = () => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.closeTravelHub();
  };

  const handleBuyItem = (index, quantity) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.buyItem(index, quantity);
  };

  const handleSellItem = (itemId, quantity, instanceId) => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.sellItem(itemId, quantity, instanceId);
  };

  const handleCloseShop = () => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.closeShop();
  };

  const handleRefreshShop = () => {
    const scene = gameRef.current?.scene.getScene("MainScene");
    if (scene) scene.refreshShop();
  };

  const handleResumeGame = (game) => {
    setHeroId((game.playerState && game.playerState.heroId) || "hero1");
    setResumeSave(game);
    setLevelReady(false);
    setPhase("playing");
  };

  const handleAbandonGame = (gameId) => {
    abandonGame(gameId)
      .then(() => loadGamesList())
      .catch((err) => console.warn("[Arpg] echec abandon de partie", err));
  };

  const handleDeleteGame = (gameId) => {
    deleteGame(gameId)
      .then(() => loadGamesList())
      .catch((err) => console.warn("[Arpg] echec suppression de partie", err));
  };

  const handleNewGame = () => setPhase("select");

  const handleSelectHero = (id) => {
    setHeroId(id);
    setResumeSave(null);

    setPlayerHp({ hp: 100, maxHp: 100 });
    setPlayerMana({ mana: 0, maxMana: 0 });
    setPlayerStamina({ stamina: 0, maxStamina: 0 });
    setXp(0);
    setLevel(1);
    setDepth(1);
    setGameOver(null);
    setLoadError(null);
    setMinimapData(null);
    setNpcDialog(null);
    setQuests({});
    setChestScreenData(null);
    setUpstairsPrompt(null);
    setExitPrompt(null);
    setResummonPrompt(null);
    setInventory([]);
    setEquipped({
      mainHand: null,
      offHand: null,
      armor: null,
      helmet: null,
      pants: null,
      boots: null,
      belt: null,
      ring1: null,
      ring2: null,
      necklace: null,
      quiver: null,
      tool: null,
    });
    setInventoryOpen(false);
    setTravelDestinations(null);
    setShopStock(null);
    setQuestsOpen(false);
    setLootToast(null);
    setMinimapVisible(true);
    setHotbarSlots(new Array(9).fill(null));
    setCooldownEvents({});
    setUnlockedAbilities([]);
    setHotbarScreenOpen(false);
    setFuryProgress({ count: 0, required: 10 });
    setUnlockedRecipes([]);
    setDiscoveredLockedRecipes([]);
    setCraftingScreenOpen(false);
    setLevelReady(false);
    setLevelUpAvailable(false);
    setLevelUpScreenOpen(false);
    setLevelUpDraft({ confirmed: {}, draft: {}, unspent: 0 });

    setPhase("playing");
  };

  if (phase === "loading") {
    return <div style={{ padding: 40, color: "#eee" }}>Chargement...</div>;
  }

  if (phase === "picker") {
    return (
      <div style={{ position: "relative" }}>
        <GameListScreen
          games={games}
          username={username}
          onResume={handleResumeGame}
          onAbandon={handleAbandonGame}
          onDelete={handleDeleteGame}
          onNewGame={handleNewGame}
          onOpenBestiary={() => setPhase("bestiary")}
          onOpenBiomes={() => setPhase("biomes")}
        />
      </div>
    );
  }

  if (phase === "bestiary") {
    return <BestiaryScreen onClose={() => setPhase("picker")} />;
  }
  if (phase === "biomes") {
    return <BiomeGalleryScreen onClose={() => setPhase("picker")} />;
  }

  if (phase === "select") {
    return (
      <CharacterSelectScreen onSelect={handleSelectHero} isMobile={isMobile} />
    );
  }

  if (isMobile && isPortrait) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          color: "#eee",
          background: "#0b0c10",
          textAlign: "center",
          padding: 20,
        }}
      >
        <div style={{ fontSize: 48, marginBottom: 16 }}>📱↻</div>
        <div style={{ fontSize: 16 }}>Tourne ton appareil pour jouer</div>
        <div style={{ fontSize: 13, color: "#999", marginTop: 8 }}>
          Ce jeu se joue en mode paysage
        </div>
      </div>
    );
  }

  const xpProgress = computeLevelFromXp(xp);

  return (
    <div
      ref={arpgContainerRef}
      className={isMobile ? "arpg arpg-mobile" : "arpg"}
      style={{
        position: "relative",
        width: isMobile || isFullscreen ? "100%" : 1000,
        height: isMobile ? "100dvh" : isFullscreen ? "100vh" : undefined,
        display: isMobile || isFullscreen ? "flex" : undefined,
        flexDirection: isMobile || isFullscreen ? "column" : undefined,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div
        className={isMobile ? "arpg-hud arpg-hud-mobile" : "arpg-hud"}
        style={{
          display: "flex",
          gap: isMobile ? 8 : 20,
          marginBottom: isMobile ? 0 : 6,
          fontSize: isMobile ? 12 : 14,
          alignItems: "center",
          flexShrink: 0,
          flexWrap: "nowrap",
          boxSizing: "border-box",
        }}
      >
        <span>Étage : {depth}</span>
        <span>Niv. : {level}</span>

        <div style={{ display: "flex", gap: 4 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <StatBar
              value={playerHp.hp}
              max={playerHp.maxHp}
              color="#c0392b"
              width={isMobile ? 70 : 90}
              label={`${Math.round(Math.max(0, playerHp.hp))}/${playerHp.maxHp}`}
            />
            <StatBar
              value={xpProgress.xpIntoLevel}
              max={xpProgress.xpForNextLevel}
              color="#d4af37"
              width={isMobile ? 70 : 90}
              label={`${xpProgress.xpIntoLevel}/${xpProgress.xpForNextLevel}`}
            />
          </div>

          {(playerMana.maxMana > 0 || playerStamina.maxStamina > 0) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {playerMana.maxMana > 0 && (
                <StatBar
                  value={playerMana.mana}
                  max={playerMana.maxMana}
                  color="#2980b9"
                  width={isMobile ? 70 : 90}
                  label={`${Math.round(Math.max(0, playerMana.mana))}/${playerMana.maxMana}`}
                />
              )}
              {playerStamina.maxStamina > 0 && (
                <StatBar
                  value={playerStamina.stamina}
                  max={playerStamina.maxStamina}
                  color="#27ae60"
                  width={isMobile ? 70 : 90}
                  label={`${Math.round(Math.max(0, playerStamina.stamina))}/${playerStamina.maxStamina}`}
                />
              )}
            </div>
          )}
        </div>
        <span
          title={`Furie : ${furyProgress.count}/${furyProgress.required} ennemis`}
        >
          🔥 {furyProgress.count}/{furyProgress.required}
        </span>

        <button
          onClick={() => setMinimapVisible((v) => !v)}
          style={{
            marginLeft: "auto",
            padding: isMobile ? "4px 7px" : "4px 12px",
            fontSize: isMobile ? 14 : 13,
            borderRadius: 6,
            border: "1px solid #555",
            background: "#2a2a35",
            color: "#eee",
            cursor: "pointer",
            flexShrink: 0,
          }}
          title="Carte"
        >
          <span className="desktop-button-label">
            🗺️
            <br /> mini Carte
          </span>
          <span className="mobile-button-icon">mini🗺️</span>
        </button>
        <button
          onClick={handleOpenFullMap}
          style={{
            padding: isMobile ? "4px 7px" : "4px 12px",
            fontSize: isMobile ? 14 : 13,
            borderRadius: 6,
            border: "1px solid #555",
            background: "#2a2a35",
            color: "#eee",
            cursor: "pointer",
            flexShrink: 0,
          }}
          title="Carte complète"
        >
          <span className="desktop-button-label">
            🗺️ Carte <br /> complète
          </span>
          <span className="mobile-button-icon">full🗺️</span>
        </button>
        {!isMobile && (
          <button
            onClick={handleToggleKeyboardLayout}
            title="Basculer la disposition clavier"
            style={{
              marginLeft: "auto",
              padding: "4px 12px",
              fontSize: 13,
              borderRadius: 6,
              border: "1px solid #555",
              background: "#2a2a35",
              color: "#eee",
              cursor: "pointer",
            }}
          >
            ⌨️ {keyboardLayout === "azerty" ? "ZQSD" : "WASD"}
          </button>
        )}

        <button
          onClick={handleOpenQuests}
          style={{
            padding: isMobile ? "4px 7px" : "4px 12px",
            fontSize: isMobile ? 14 : 13,
            borderRadius: 6,
            border: "1px solid #555",
            background: "#2a2a35",
            color: "#eee",
            cursor: "pointer",
            flexShrink: 0,
          }}
          title="Quêtes"
        >
          {isMobile ? "📜" : "📜"}
        </button>

        <button
          onClick={handleOpenInventory}
          style={{
            padding: isMobile ? "4px 7px" : "4px 12px",
            fontSize: isMobile ? 14 : 13,
            borderRadius: 6,
            border: "1px solid #555",
            background: "#2a2a35",
            color: "#eee",
            cursor: "pointer",
            flexShrink: 0,
          }}
          title="Inventaire"
        >
          {isMobile ? "🎒" : "🎒"}
        </button>

        <button
          onClick={handleOpenHotbarScreen}
          style={{
            padding: isMobile ? "4px 7px" : "4px 12px",
            fontSize: isMobile ? 14 : 13,
            borderRadius: 6,
            border: "1px solid #555",
            background: "#2a2a35",
            color: "#eee",
            cursor: "pointer",
            flexShrink: 0,
          }}
          title="Raccourcis"
        >
          {isMobile ? "⚡" : "⚡"}
        </button>

        <button
          onClick={handleOpenCraftingScreen}
          style={{
            padding: isMobile ? "4px 7px" : "4px 12px",
            fontSize: isMobile ? 14 : 13,
            borderRadius: 6,
            border: "1px solid #555",
            background: "#2a2a35",
            color: "#eee",
            cursor: "pointer",
            flexShrink: 0,
          }}
          title="Craft"
        >
          {isMobile ? "🔨" : "🔨"}
        </button>

        <button
          onClick={handleSaveAndQuit}
          style={{
            padding: isMobile ? "4px 7px" : "4px 12px",
            fontSize: isMobile ? 14 : 13,
            borderRadius: 6,
            border: "1px solid #555",
            background: "#2a2a35",
            color: "#eee",
            cursor: "pointer",
            flexShrink: 0,
          }}
          title="Sauvegarder et quitter"
        >
          {isMobile ? "💾" : "💾 et quitter"}
        </button>
      </div>

      {lootToast && (
        <div
          style={{
            position: "fixed",
            top: isMobile ? 60 : 44,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 999,
            padding: isMobile ? "5px 10px" : "6px 16px",
            fontSize: isMobile ? 11 : 13,
            borderRadius: 6,
            background: "rgba(20,18,14,0.9)",
            border: "1px solid #8a7050",
            color: "#f0e6d0",
            pointerEvents: "none",
            whiteSpace: "nowrap",
            maxWidth: isMobile ? "80vw" : undefined,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {lootToast}
        </div>
      )}

      <div
        className={
          isMobile ? "arpg-game-area arpg-game-area-mobile" : "arpg-game-area"
        }
        style={{
          position: "relative",
          flex: isMobile || isFullscreen ? 1 : undefined,
          minHeight: isMobile || isFullscreen ? 0 : undefined,
          overflow: "hidden",
        }}
      >
        <button
          onClick={handleToggleFullscreen}
          style={{
            position: "absolute",
            top: 8,
            left: 8,
            zIndex: 6,
            width: 32,
            height: 32,
            borderRadius: 6,
            border: "1px solid #555",
            background: "rgba(30,32,41,0.7)",
            color: "#eee",
            cursor: "pointer",
            fontSize: 15,
          }}
          title={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
        >
          {isFullscreen ? "⤡" : "⤢"}
        </button>
        <div
          ref={containerRef}
          id="arpg-container"
          className="arpg-container"
          style={
            isMobile || isFullscreen
              ? {
                  width: "100%",
                  height: "100%",
                  position: "absolute",
                  inset: 0,
                }
              : undefined
          }
        />

        {isMobile && (
          <TouchControls
            gameRef={gameRef}
            furyReady={furyProgress.count >= furyProgress.required}
          />
        )}

        {!levelReady && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 30,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "#12131a",
              color: "#eee",
              gap: 16,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                border: "4px solid #333",
                borderTopColor: "#8a7050",
                borderRadius: "50%",
                animation: "arpg-spin 0.8s linear infinite",
              }}
            />
            <div style={{ fontSize: 14, color: "#999" }}>
              Chargement de l'étage...
            </div>
            <style>{`
              @keyframes arpg-spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}

        {minimapVisible && minimapData && (
          <div className="arpg-minimap">
            <Minimap
              grid={minimapData.grid}
              fogState={minimapData.fogState}
              playerTile={minimapData.playerTile}
              exitTile={minimapData.exitTile}
              upstairsTile={minimapData.upstairsTile}
              questNpcs={minimapData.questNpcs || []}
              summons={minimapData.summons || []}
              isMobile={isMobile}
            />
          </div>
        )}

        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 15,
            display: "flex",
            alignItems: "center",
            gap: 14,
            pointerEvents: "auto",
          }}
        >
          <div style={{ display: "flex", gap: 6 }}>
            {hotbarSlots.map((slot, index) => {
              const iconId = slot
                ? slot.type === "item"
                  ? slot.itemId
                  : slot.id
                : null;
              const showIcon = iconId && hasIconFrame(iconId);
              const label = slot
                ? slot.type === "ability"
                  ? resolveAbilityDef(slot.id).name
                  : resolveItemDef(slot.itemId).name
                : null;
              const cooldownKey = slot
                ? slot.type === "ability"
                  ? `ability:${slot.id}`
                  : `item:${slot.itemId}`
                : null;
              const cooldownInfo = cooldownKey
                ? cooldownEvents[cooldownKey]
                : null;
              const quantity =
                slot?.type === "item"
                  ? (inventory.find((i) => i.itemId === slot.itemId)
                      ?.quantity ?? 0)
                  : null;

              return (
                <div
                  key={index}
                  onClick={() => {
                    const scene = gameRef.current?.scene.getScene("MainScene");
                    if (scene) scene.useHotbarSlot(index);
                  }}
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 6,
                    border: "1px solid #555",
                    background: slot
                      ? "rgba(58,47,32,0.9)"
                      : "rgba(30,32,41,0.6)",
                    color: "#f0e6d0",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 9,
                    textAlign: "center",
                    padding: 2,
                    boxSizing: "border-box",
                    position: "relative",
                    overflow: "hidden",
                    cursor: slot ? "pointer" : "default",
                  }}
                  title={label || "Vide"}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 1,
                      left: 3,
                      fontSize: 10,
                      color: "#8a7050",
                    }}
                  >
                    {index + 1}
                  </div>
                  {showIcon ? (
                    <ItemIcon itemId={iconId} scale={1.3} />
                  ) : (
                    label && (
                      <div
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          width: "100%",
                        }}
                      >
                        {label}
                      </div>
                    )
                  )}
                  {quantity !== null && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 1,
                        right: 3,
                        fontSize: 9,
                        color: "#f0e6d0",
                        textShadow: "0 0 2px #000, 0 0 2px #000",
                      }}
                    >
                      x{quantity}
                    </div>
                  )}
                  {cooldownInfo && (
                    <HotbarCooldownOverlay
                      key={cooldownInfo.startedAt}
                      startedAt={cooldownInfo.startedAt}
                      cooldownMs={cooldownInfo.cooldownMs}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <button
            onClick={handleOpenLevelUpScreen}
            style={{
              width: 42,
              height: 42,
              borderRadius: 6,
              border: levelUpAvailable ? "2px solid #ffd700" : "1px solid #555",
              background: levelUpAvailable ? "#3a3320" : "rgba(30,32,41,0.6)",
              color: "#f0e6d0",
              fontSize: 20,
              cursor: "pointer",
              flexShrink: 0,
              animation: levelUpAvailable
                ? "arpg-pulse 1s ease-in-out infinite"
                : "none",
            }}
            title="Monter de niveau"
          >
            +
          </button>
          <style>{`
    @keyframes arpg-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
  `}</style>
        </div>
        {loadError && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.7)",
              color: "#fff",
            }}
          >
            Erreur de chargement du niveau : {loadError}
          </div>
        )}

        {gameOver && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.7)",
              color: "#fff",
              fontSize: 22,
              gap: 14,
            }}
          >
            <div>Game over</div>
            <div style={{ fontSize: 14, color: "#ccc" }}>
              Étage {gameOver.depth} - XP total : {gameOver.xp}
            </div>
            <button
              onClick={handleRetry}
              style={{
                padding: "8px 18px",
                fontSize: 14,
                borderRadius: 6,
                border: "1px solid #555",
                background: "#2a2a35",
                color: "#eee",
                cursor: "pointer",
              }}
            >
              Réessayer cet étage
            </button>
          </div>
        )}

        {npcDialog && (
          <div
            style={{
              position: "absolute",
              bottom: isMobile ? 100 : 20,
              left: isMobile ? 10 : 20,
              right: isMobile ? 10 : 20,
              zIndex: 20,
              background: "rgba(20,18,15,0.95)",
              border: "2px solid #8a7050",
              borderRadius: 8,
              padding: isMobile ? 12 : 16,
              color: "#f0e6d0",
              fontSize: isMobile ? 13 : 14,
            }}
          >
            <div style={{ marginBottom: 12, whiteSpace: "pre-line" }}>
              {npcDialog.text}
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {(npcDialog.choices || []).map((c, i) => (
                <button
                  key={i}
                  onClick={() => handleChooseInteraction(i)}
                  style={{
                    padding: "6px 14px",
                    fontSize: 13,
                    borderRadius: 6,
                    border: "1px solid #8a7050",
                    background: "#3a2f20",
                    color: "#f0e6d0",
                    cursor: "pointer",
                    outline:
                      getCurrentDialogActions()[focusedButtonIndex]?.label ===
                      `choice-${i}`
                        ? "3px solid #fff"
                        : "none",
                  }}
                >
                  {c.label}
                </button>
              ))}
              {npcDialog.canAccept && (
                <button
                  onClick={handleAcceptQuest}
                  style={{
                    padding: "6px 14px",
                    fontSize: 13,
                    borderRadius: 6,
                    border: "1px solid #8a7050",
                    background: "#3a2f20",
                    color: "#f0e6d0",
                    cursor: "pointer",
                    outline:
                      getCurrentDialogActions()[focusedButtonIndex]?.label ===
                      "Accepter"
                        ? "3px solid #fff"
                        : "none",
                  }}
                >
                  Accepter
                </button>
              )}
              {npcDialog.canTurnIn && (
                <button
                  onClick={handleTurnInQuest}
                  style={{
                    padding: "6px 14px",
                    fontSize: 13,
                    borderRadius: 6,
                    border: "1px solid #8a7050",
                    background: "#3a2f20",
                    color: "#f0e6d0",
                    cursor: "pointer",
                    outline:
                      getCurrentDialogActions()[focusedButtonIndex]?.label ===
                      "Rendre"
                        ? "3px solid #fff"
                        : "none",
                  }}
                >
                  Rendre
                </button>
              )}
              <button
                onClick={handleCloseDialog}
                style={{
                  padding: "6px 14px",
                  fontSize: 13,
                  borderRadius: 6,
                  border: "1px solid #555",
                  background: "#2a2a35",
                  color: "#eee",
                  cursor: "pointer",
                  outline:
                    getCurrentDialogActions()[focusedButtonIndex]?.label ===
                    "Fermer"
                      ? "3px solid #fff"
                      : "none",
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        )}

        {upstairsPrompt && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.75)",
              color: "#fff",
              fontSize: 18,
              gap: 16,
            }}
          >
            <div>Redescendre à l'étage précédent ?</div>
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={handleConfirmUpstairs}
                style={{
                  padding: "8px 20px",
                  fontSize: 14,
                  borderRadius: 6,
                  border: "1px solid #dc3030",
                  background: "#3a1f1f",
                  color: "#f0d0d0",
                  cursor: "pointer",
                  outline: focusedButtonIndex === 0 ? "3px solid #fff" : "none",
                }}
              >
                Oui
              </button>
              <button
                onClick={handleCancelUpstairs}
                style={{
                  padding: "8px 20px",
                  fontSize: 14,
                  borderRadius: 6,
                  border: "1px solid #555",
                  background: "#2a2a35",
                  color: "#eee",
                  cursor: "pointer",
                  outline: focusedButtonIndex === 1 ? "3px solid #fff" : "none",
                }}
              >
                Non
              </button>
            </div>
          </div>
        )}
        {exitPrompt && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.75)",
              color: "#fff",
              fontSize: 18,
              gap: 16,
            }}
          >
            <div>Monter à l'étage suivant ?</div>
            {(exitPrompt.remainingEnemies > 0 ||
              exitPrompt.unopenedChests > 0 ||
              exitPrompt.partiallyLootedChests > 0) && (
              <div
                style={{
                  fontSize: 13,
                  color: "#ffcc66",
                  textAlign: "center",
                  maxWidth: 340,
                }}
              >
                <div>
                  ⚠️{" "}
                  {exitPrompt.remainingEnemies > 0 &&
                    `${exitPrompt.remainingEnemies} ennemi${exitPrompt.remainingEnemies > 1 ? "s" : ""} restant${exitPrompt.remainingEnemies > 1 ? "s" : ""}`}
                  {exitPrompt.remainingEnemies > 0 &&
                    exitPrompt.unopenedChests > 0 &&
                    " · "}
                  {exitPrompt.unopenedChests > 0 &&
                    `${exitPrompt.unopenedChests} coffre${exitPrompt.unopenedChests > 1 ? "s" : ""} non ouvert${exitPrompt.unopenedChests > 1 ? "s" : ""}`}
                </div>
                {exitPrompt.partiallyLootedChests > 0 && (
                  <div style={{ color: "#ff8888", marginTop: 4 }}>
                    {exitPrompt.partiallyLootedChests} coffre
                    {exitPrompt.partiallyLootedChests > 1 ? "s" : ""} avec du
                    butin non récupéré — ce butin sera perdu si tu descends
                  </div>
                )}
              </div>
            )}
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={handleConfirmExit}
                style={{
                  padding: "8px 20px",
                  fontSize: 14,
                  borderRadius: 6,
                  border: "1px solid #ffd700",
                  background: "#3a3320",
                  color: "#f0e8c0",
                  cursor: "pointer",
                  outline: focusedButtonIndex === 0 ? "3px solid #fff" : "none",
                }}
              >
                Oui
              </button>
              <button
                onClick={handleCancelExit}
                style={{
                  padding: "8px 20px",
                  fontSize: 14,
                  borderRadius: 6,
                  border: "1px solid #555",
                  background: "#2a2a35",
                  color: "#eee",
                  cursor: "pointer",
                  outline: focusedButtonIndex === 1 ? "3px solid #fff" : "none",
                }}
              >
                Non
              </button>
            </div>
          </div>
        )}
        {summonReplacePrompt && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.75)",
              color: "#fff",
              fontSize: 18,
              gap: 16,
            }}
          >
            <div>
              {summonReplacePrompt.victimName} va disparaître pour laisser la
              place à {summonReplacePrompt.newName}. Continuer ?
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={handleConfirmSummonReplace}
                style={{
                  padding: "8px 20px",
                  fontSize: 14,
                  borderRadius: 6,
                  border: "1px solid #ffd700",
                  background: "#3a3320",
                  color: "#f0e8c0",
                  cursor: "pointer",
                  outline: focusedButtonIndex === 0 ? "3px solid #fff" : "none",
                }}
              >
                Oui
              </button>
              <button
                onClick={handleCancelSummonReplace}
                style={{
                  padding: "8px 20px",
                  fontSize: 14,
                  borderRadius: 6,
                  border: "1px solid #555",
                  background: "#2a2a35",
                  color: "#eee",
                  cursor: "pointer",
                  outline: focusedButtonIndex === 1 ? "3px solid #fff" : "none",
                }}
              >
                Non
              </button>
            </div>
          </div>
        )}
        {inventoryOpen && (
          <InventoryScreen
            inventory={inventory}
            equipped={equipped}
            stats={combatStats}
            heroId={heroId}
            isMobile={isMobile}
            unlockedRecipes={unlockedRecipes}
            onEquip={handleEquip}
            onUnequip={handleUnequip}
            onUse={handleUseConsumable}
            onDecraft={handleDecraftItem}
            onSocketGem={handleSocketGem}
            onRename={handleRenameEquipment}
            onAttemptPerforation={handleAttemptPerforation}
            onAttemptGemExtraction={handleAttemptGemExtraction}
            onClose={handleCloseInventory}
          />
        )}
        {chestScreenData && (
          <ChestScreen
            title={chestScreenData.title}
            items={chestScreenData.items}
            focusedLabel={getCurrentDialogActions()[focusedButtonIndex]?.label}
            onTakeItem={handleTakeChestItem}
            onTakeAll={handleTakeAllChest}
            onClose={handleCloseChestScreen}
          />
        )}
        {fullMapOpen && (
          <FullMapScreen
            grid={minimapData?.grid}
            fogState={minimapData?.fogState}
            playerTile={minimapData?.playerTile}
            exitTile={minimapData?.exitTile}
            upstairsTile={minimapData?.upstairsTile}
            questNpcs={minimapData?.questNpcs || []}
            summons={minimapData?.summons || []}
            bossDoorTile={minimapData?.bossDoorTile}
            bossRoomOpen={minimapData?.bossRoomOpen}
            onClose={handleCloseFullMap}
          />
        )}
        {questsOpen && (
          <QuestsScreen
            quests={quests}
            inventory={inventory}
            onClose={handleCloseQuests}
          />
        )}
        {hotbarScreenOpen && (
          <HotbarScreen
            hotbarSlots={hotbarSlots}
            unlockedAbilities={unlockedAbilities}
            inventory={inventory}
            playerLevel={level}
            onAssign={handleAssignHotbarSlot}
            onClose={handleCloseHotbarScreen}
          />
        )}
        {craftingScreenOpen && (
          <CraftingScreen
            unlockedRecipes={unlockedRecipes}
            discoveredLockedRecipes={discoveredLockedRecipes}
            inventory={inventory}
            equipped={equipped}
            isMobile={isMobile}
            onCraft={handleCraftItem}
            onFreeCraft={handleAttemptFreeCraft}
            onClose={handleCloseCraftingScreen}
          />
        )}
        {levelUpScreenOpen && (
          <AttributesScreen
            confirmedAttributes={levelUpDraft.confirmed}
            draftAttributes={levelUpDraft.draft}
            unspent={levelUpDraft.unspent}
            focusedLabel={getCurrentDialogActions()[focusedButtonIndex]?.label}
            onAllocate={handleAllocatePoint}
            onDeallocate={handleDeallocatePoint}
            onConfirm={handleConfirmAllocation}
            onClose={handleCloseLevelUpScreen}
          />
        )}
        {travelDestinations && (
          <TravelHubScreen
            destinations={travelDestinations}
            onTravel={handleTravelToDepth}
            onClose={handleCloseTravelHub}
          />
        )}
        {shopStock && (
          <ShopScreen
            stock={shopStock}
            inventory={inventory}
            equipped={equipped}
            refreshCost={
              gameRef.current?.scene
                ?.getScene("MainScene")
                ?.getShopRefreshCost?.() ?? 0
            }
            onBuy={handleBuyItem}
            onSell={handleSellItem}
            onRefresh={handleRefreshShop}
            onClose={handleCloseShop}
          />
        )}
      </div>
    </div>
  );
}
